import type { MutationFunction, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { BodyType, ErrorType } from '../axios';
import { request } from '../axios';
import type { CreateMedicationDto } from '../generated/model';

type RequestOptions = Parameters<typeof request>[1];

export const updateMedication = (
  id: string,
  createMedicationDto: BodyType<CreateMedicationDto>,
  options?: RequestOptions,
) => {
  return request<void>(
    {
      url: `/api/medication/${id}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: createMedicationDto,
    },
    options,
  );
};

export const getUpdateMedicationMutationOptions = <
  TData = Awaited<ReturnType<typeof updateMedication>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<CreateMedicationDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateMedication'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMedication>>,
    { id: string; data: BodyType<CreateMedicationDto> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateMedication(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<CreateMedicationDto> },
    TContext
  >;
};

export type UpdateMedicationMutationResult = NonNullable<Awaited<ReturnType<typeof updateMedication>>>;
export type UpdateMedicationMutationBody = BodyType<CreateMedicationDto>;
export type UpdateMedicationMutationError = ErrorType<unknown>;

export const useUpdateMedication = <
  TData = Awaited<ReturnType<typeof updateMedication>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<CreateMedicationDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<CreateMedicationDto> }, TContext> => {
  const mutationOptions = getUpdateMedicationMutationOptions(options);

  return useMutation(mutationOptions);
};
